import React, { useState, useEffect, useRef, useCallback } from 'react';
import StationAvatar from './StationAvatar';
import apiService from '../services/api';
import { EXTERNAL_URLS } from '../constants';

const GENRES = [
  { label: 'All', value: '' },
  { label: 'Pop', value: 'pop' },
  { label: 'Rock', value: 'rock' },
  { label: 'News', value: 'news' },
  { label: 'Jazz', value: 'jazz' },
  { label: 'Classical', value: 'classical' },
  { label: 'Devotional', value: 'devotional' },
  { label: 'Electronic', value: 'electronic' },
  { label: 'Folk', value: 'folk' },
  { label: 'Hip-hop', value: 'hip-hop' },
];

const GENRE_SECTIONS = [
  { key: 'news',       label: 'News & Talk',           query: 'news' },
  { key: 'pop',        label: 'Pop Music',              query: 'pop' },
  { key: 'hindi',      label: 'Hindi & Bollywood',      query: 'hindi' },
  { key: 'devotional', label: 'Devotional & Spiritual', query: 'devotional' },
  { key: 'rock',       label: 'Rock',                   query: 'rock' },
  { key: 'jazz',       label: 'Jazz',                   query: 'jazz' },
  { key: 'classical',  label: 'Classical',              query: 'classical' },
  { key: 'electronic', label: 'Electronic & Dance',     query: 'electronic' },
  { key: 'hiphop',     label: 'Hip-Hop & R&B',          query: 'hip-hop' },
  { key: 'folk',       label: 'Folk & World Music',     query: 'folk' },
  { key: 'sports',     label: 'Sports',                 query: 'sports' },
  { key: 'talk',       label: 'Talk & Podcast',         query: 'talk' },
  { key: 'lounge',     label: 'Lounge & Chill',         query: 'lounge' },
  { key: 'reggae',     label: 'Reggae',                 query: 'reggae' },
];

/* ── IP-based country detection (accurate, no user permission needed) ── */
async function detectCountryByIP(countries) {
  try {
    const res = await fetch(EXTERNAL_URLS.COUNTRY_BY_IP + '/', { signal: AbortSignal.timeout(3000) });
    const data = await res.json();
    const code = data?.country;
    if (code) {
      const match = countries.find(
        (c) => c.iso_code && c.iso_code.toUpperCase() === code.toUpperCase()
      );
      if (match) return match.name;
    }
  } catch {
    // IP API failed — fall back to browser locale(s)
  }
  // Fallback: try all preferred locales (e.g. en-IN, hi-IN) so India → IN, not en-US → US
  const locales = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const locale of locales) {
    const regionCode = (locale || '').split('-')[1];
    if (regionCode && regionCode.length >= 2) {
      const match = countries.find(
        (c) => c.iso_code && c.iso_code.toUpperCase() === regionCode.toUpperCase()
      );
      if (match) return match.name;
    }
  }
  return null;
}

/* ── Calculate how many cards fit in exactly 1 row ── */
function calcGridCount(containerWidth) {
  const cardW = 108; // compact card ~96px + 12px gap
  return Math.max(3, Math.floor((containerWidth + 12) / cardW));
}

/* ── Station mini-card ── */
const StationMiniCard = ({ station, isPlaying, onPlay, isFavorite, onToggleFavorite }) => {
  const playing = isPlaying(station);
  const meta = station.country_name || station.country || 'Radio';

  return (
    <div
      className={`station-mini-card${playing ? ' is-playing' : ''}`}
      onClick={() => onPlay(station)}
      role="button"
      tabIndex={0}
      aria-label={`${station.name}${playing ? ', now playing — click to pause' : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlay(station); }
      }}
    >
      {/* Avatar with play/pause overlay on hover */}
      <div className="station-mini-avatar-wrap">
        <StationAvatar
          name={station.name}
          favicon={station.favicon}
          homepage={station.homepage}
          playing={playing}
          size={44}
        />
        <div className="station-mini-overlay" aria-hidden="true">
          {playing ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21"/>
            </svg>
          )}
        </div>
      </div>

      {onToggleFavorite && (
        <button
          className={`station-mini-favorite${isFavorite ? ' is-active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(station); }}
          aria-label={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
          tabIndex={-1}
        >
          {isFavorite ? '♥' : '♡'}
        </button>
      )}

      <div className="station-mini-name">{station.name}</div>
      <div className="station-mini-meta">{meta}</div>
    </div>
  );
};

/* ── Country mini-card ── */
const CountryMiniCard = ({ country, getCountryFlag, onSelect }) => (
  <div
    className="country-mini-card"
    onClick={() => onSelect(country)}
    role="button"
    tabIndex={0}
    aria-label={`${country.name} — ${country.station_count} stations`}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(country); }
    }}
  >
    <span className="country-mini-flag">{getCountryFlag(country.iso_code)}</span>
    <span className="country-mini-name">{country.name}</span>
    <span className="country-mini-count">{country.station_count}</span>
  </div>
);

/* ── Station grid — 1 row with optional "See all" that opens a dedicated page ── */
const StationGrid = ({ stations, loading, isPlaying, onPlay, onSeeAll, onCountChange }) => {
  const containerRef = useRef(null);
  const [count, setCount] = useState(6);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const next = calcGridCount(containerRef.current.offsetWidth);
        setCount(next);
        if (onCountChange) onCountChange(next);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [onCountChange]);

  if (loading) {
    return (
      <div className="station-grid" ref={containerRef}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="station-mini-card station-mini-skeleton" />
        ))}
      </div>
    );
  }
  if (!stations.length) return <div className="station-grid" ref={containerRef} />;

  const visibleCount = Math.min(stations.length, count);

  return (
    <div className="station-grid" ref={containerRef}>
      {stations.slice(0, visibleCount).map((s, i) => (
        <StationMiniCard
          key={s.id || i}
          station={s}
          isPlaying={isPlaying}
          onPlay={onPlay}
          isFavorite={stations.isFavorite && stations.isFavorite(s)}
          onToggleFavorite={stations.onToggleFavorite}
        />
      ))}
    </div>
  );
};

/* ── Section wrapper: title + conditional "See all" + grid ── */
const HomeSection = ({ title, stations, loading, isPlaying, onPlay, onSeeAll, headerExtra }) => {
  const [rowCount, setRowCount] = useState(6);
  const showSeeAll = onSeeAll && !loading && stations.length > rowCount;

  return (
    <section className="home-section">
      <div className="home-section-header">
        <h2 className="home-section-title">{title}</h2>
        {headerExtra}
        {showSeeAll && (
          <button className="home-section-link" type="button" onClick={onSeeAll}>
            See all →
          </button>
        )}
      </div>
      <StationGrid
        stations={stations}
        loading={loading}
        isPlaying={isPlaying}
        onPlay={onPlay}
        onCountChange={setRowCount}
      />
    </section>
  );
};

/* ── Main HomePage ── */
const HomePage = ({
  countries,
  currentPlayingStation,
  isPlaying,
  audioRef,
  getCountryFlag,
  favoriteStations = [],
  recentStations = [],
  onStationSelect,
  onToggleFavorite,
  onOpenCollection,
  onCountrySelect,
  onBrowseAllCountries,
}) => {
  const [searchTerm, setSearchTerm]       = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen]       = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const [activeGenre, setActiveGenre] = useState('');

  const [nearYouStations, setNearYouStations] = useState([]);
  const [nearYouLoading, setNearYouLoading]   = useState(false);
  const [nearYouCountry, setNearYouCountry]   = useState('');
  const [locationLoading, setLocationLoading] = useState(true);

  const [worldwideStations, setWorldwideStations] = useState([]);
  const [worldwideLoading, setWorldwideLoading]   = useState(false);

  // Genre section states: { news: [], pop: [], ... }
  const [genreStations, setGenreStations]   = useState({});
  const [genreLoading, setGenreLoading]     = useState({});

  const searchRef   = useRef(null);
  const searchTimer = useRef(null);

  /* ── Is playing check ── */
  const isStationPlaying = useCallback(
    (station) =>
      currentPlayingStation?.name === station.name &&
      isPlaying &&
      audioRef.current &&
      !audioRef.current.paused,
    [currentPlayingStation, isPlaying, audioRef]
  );

  /* ── IP-based location (no infinite loading when countries failed to load) ── */
  useEffect(() => {
    if (!countries.length) {
      setLocationLoading(false);
      return;
    }
    setLocationLoading(true);
    detectCountryByIP(countries).then((name) => {
      setNearYouCountry(name || '');
      setLocationLoading(false);
    });
  }, [countries]);

  /* ── Load near-you stations when country or genre changes ── */
  const loadNearYou = useCallback(async (countryName, genre) => {
    if (!countryName) return;
    setNearYouLoading(true);
    try {
      const data = await apiService.getStations(countryName, genre || '', 1, 30);
      setNearYouStations(data.results || []);
    } catch {
      setNearYouStations([]);
    } finally {
      setNearYouLoading(false);
    }
  }, []);

  useEffect(() => {
    if (nearYouCountry) loadNearYou(nearYouCountry, activeGenre);
  }, [nearYouCountry, activeGenre, loadNearYou]);

  /* ── Worldwide popular ── */
  useEffect(() => {
    setWorldwideLoading(true);
    apiService
      .getStations('', '', 1, 30)
      .then((d) => setWorldwideStations(d.results || []))
      .catch(() => setWorldwideStations([]))
      .finally(() => setWorldwideLoading(false));
  }, []);
  const worldwideFailed = !worldwideLoading && worldwideStations.length === 0;

  /* ── Genre sections ── */
  useEffect(() => {
    GENRE_SECTIONS.forEach(({ key, query }) => {
      setGenreLoading((prev) => ({ ...prev, [key]: true }));
      apiService
        .getStations('', query, 1, 30)
        .then((d) => setGenreStations((prev) => ({ ...prev, [key]: d.results || [] })))
        .catch(() => setGenreStations((prev) => ({ ...prev, [key]: [] })))
        .finally(() => setGenreLoading((prev) => ({ ...prev, [key]: false })));
    });
  }, []);

  /* ── Search ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(searchTimer.current);
    if (val.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    setSearchOpen(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const data = await apiService.searchStations(val.trim());
        setSearchResults(data.results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handleSearchSelect = (station) => {
    onStationSelect(station);
    setSearchTerm('');
    setSearchOpen(false);
    setSearchResults([]);
  };

  /* Close search dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { greeting, subtitle } = getTimeContext();

  const isStationFavorite = useCallback(
    (station) =>
      favoriteStations.some((fav) =>
        (fav.id && station.id && fav.id === station.id) || fav.name === station.name
      ),
    [favoriteStations]
  );

  return (
    <div className="home-page animate-fade-in">

      {/* Hero */}
      <div className="home-hero">
        <h1 className="home-title">{greeting}</h1>
        <p className="home-subtitle">{subtitle}</p>
      </div>

      {/* Global Search */}
      <div className="home-search-wrapper" ref={searchRef}>
        <div className="home-search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search stations, genres, countries…"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={(e) => e.key === 'Escape' && (setSearchOpen(false), setSearchTerm(''))}
            onFocus={() => searchTerm.length >= 2 && setSearchOpen(true)}
            aria-label="Search stations"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={() => { setSearchTerm(''); setSearchOpen(false); setSearchResults([]); }}
              aria-label="Clear search"
            >✕</button>
          )}
        </div>
        {searchOpen && (
          <div className="search-dropdown" role="listbox">
            {searchLoading ? (
              <div className="search-dropdown-loading">Searching…</div>
            ) : searchResults.length > 0 ? (
              searchResults.map((s, i) => (
                <div
                  key={s.id || i}
                  className="search-dropdown-item"
                  role="option"
                  onClick={() => handleSearchSelect(s)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSelect(s)}
                  tabIndex={0}
                >
                  <StationAvatar name={s.name} favicon={s.favicon} homepage={s.homepage} size={36} />
                  <div className="search-dropdown-info">
                    <div className="search-dropdown-name">{s.name}</div>
                    <div className="search-dropdown-meta">
                      {[s.country_name || s.country, s.tags ? s.tags.split(',')[0].trim() : '']
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button className="search-dropdown-play" tabIndex={-1}>▶</button>
                </div>
              ))
            ) : (
              <div className="search-dropdown-empty">No stations found for "{searchTerm}"</div>
            )}
          </div>
        )}
      </div>

      {/* Genre Filter Chips */}
      <div className="genre-chips-wrapper">
        <div className="genre-chips">
          {GENRES.map((g) => (
            <button
              key={g.value}
              className={`genre-chip${activeGenre === g.value ? ' active' : ''}`}
              onClick={() => setActiveGenre(g.value)}
              aria-pressed={activeGenre === g.value}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {favoriteStations.length > 0 && (
        <HomeSection
          title="Your Favourites"
          stations={favoriteStations}
          loading={false}
          isPlaying={isStationPlaying}
          onPlay={onStationSelect}
          onSeeAll={onOpenCollection ? () => onOpenCollection('Your Favourites', 'Stations you have marked as favourite.', favoriteStations) : undefined}
        />
      )}

      {recentStations.length > 0 && (
        <HomeSection
          title="Recently Played"
          stations={recentStations}
          loading={false}
          isPlaying={isStationPlaying}
          onPlay={onStationSelect}
          onSeeAll={onOpenCollection ? () => onOpenCollection('Recently Played', 'Stations you listened to recently.', recentStations) : undefined}
        />
      )}

      {/* Top Stations Near You — needs custom empty state so can't use HomeSection fully */}
      <section className="home-section">
        <div className="home-section-header">
          <h2 className="home-section-title">
            {locationLoading ? 'Detecting your location…' : 'Top Stations Near You'}
          </h2>
          {nearYouCountry && (
            <button
              className="home-section-link"
              onClick={() => {
                const c = countries.find((x) => x.name === nearYouCountry);
                if (c) onCountrySelect(c);
              }}
            >
              See all →
            </button>
          )}
        </div>
        {!locationLoading && !nearYouCountry ? (
          <p className="home-empty-text">
            Could not detect your location.{' '}
            <button className="home-inline-link" onClick={onBrowseAllCountries}>
              Browse by country
            </button>
          </p>
        ) : (
          <StationGrid
            stations={nearYouStations}
            loading={nearYouLoading || locationLoading}
            isPlaying={isStationPlaying}
            onPlay={onStationSelect}
          />
        )}
      </section>

      {worldwideFailed ? (
        <section className="home-section">
          <div className="home-section-header">
            <h2 className="home-section-title">Popular Worldwide</h2>
          </div>
          <p className="home-empty-text">
            Stations couldn&apos;t be loaded right now. Please check your connection and refresh the page.
          </p>
        </section>
      ) : (
        <HomeSection
          title="Popular Worldwide"
          stations={worldwideStations}
          loading={worldwideLoading}
          isPlaying={isStationPlaying}
          onPlay={onStationSelect}
          onSeeAll={onOpenCollection ? () => onOpenCollection('Popular Worldwide', 'Most listened stations across all countries.', worldwideStations) : undefined}
        />
      )}

      {/* Genre Sections */}
      {GENRE_SECTIONS.map(({ key, label }) => {
        const stations = genreStations[key] || [];
        const loading  = genreLoading[key] || false;
        if (!loading && stations.length === 0) return null;
        return (
          <HomeSection
            key={key}
            title={label}
            stations={stations}
            loading={loading}
            isPlaying={isStationPlaying}
            onPlay={onStationSelect}
            onSeeAll={onOpenCollection ? () => onOpenCollection(label, `Popular ${label.toLowerCase()} stations.`, stations) : undefined}
          />
        );
      })}

      {/* Browse by Country */}
      <section className="home-section">
        <div className="home-section-header">
          <h2 className="home-section-title">Browse by Country</h2>
          <button className="home-section-link" onClick={onBrowseAllCountries}>
            See all →
          </button>
        </div>
        {countries.length > 0 ? (
          <div className="country-mini-grid">
            {countries.slice(0, 12).map((c, i) => (
              <CountryMiniCard
                key={c.id || i}
                country={c}
                getCountryFlag={getCountryFlag}
                onSelect={onCountrySelect}
              />
            ))}
          </div>
        ) : (
          <p className="home-empty-text">
            Countries couldn&apos;t be loaded. Please refresh the page or try again in a moment.
          </p>
        )}
      </section>

    </div>
  );
};

function getTimeContext() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return {
    greeting: 'Rise & Tune In',
    subtitle: 'Grab your coffee — thousands of live stations are already broadcasting.',
  };
  if (h >= 12 && h < 17) return {
    greeting: 'You\'re On Air',
    subtitle: 'Millions of listeners, one world of music. What are you in the mood for?',
  };
  if (h >= 17 && h < 21) return {
    greeting: 'Evening Session',
    subtitle: 'Kick back and let the world come to you — explore sounds from every corner of the globe.',
  };
  if (h >= 21 || h < 1) return {
    greeting: 'Night Frequencies',
    subtitle: 'The world never stops broadcasting. Discover what\'s live after dark.',
  };
  return {
    greeting: 'Night Owl Radio',
    subtitle: 'Up this late? So are thousands of stations worldwide. Start exploring.',
  };
}

export default HomePage;
